/**
 * InsightsReportView - renders one stored or freshly generated Insights_Report.
 *
 * Presentational only. Figures arrive as pre-formatted decimal strings and are
 * rendered exactly as received (no monetary arithmetic). The report is advisory:
 * nothing here submits, executes, or routes a trade.
 *
 * Requirements: 5.1-5.6, 6.6, 6.7, 6.8, 8.3, 8.5, 12.5, 12.8
 */
import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { formatDateTime } from '@/utils/formatters';
import type { InsightsReport, SectorWeight } from '@/types/insights';
import { ProvenancePanel } from './ProvenancePanel';

export interface InsightsReportViewProps {
  report: InsightsReport;
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  const headingId = `insights-${id}-heading`;
  return (
    <Card hover={false} aria-labelledby={headingId} role="region">
      <CardHeader>
        <CardTitle as="h2" id={headingId}>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function WeightList({ label, weights }: { label: string; weights: SectorWeight[] }) {
  if (weights.length === 0) {
    return <p className="text-sm text-dark-400">{label}: none.</p>;
  }
  return (
    <div>
      <h3 className="mb-1 text-sm font-medium text-dark-300">{label}</h3>
      <ul className="space-y-1 text-sm text-dark-200">
        {weights.map((w) => (
          <li key={w.sector} className="flex justify-between gap-4">
            <span>{w.sector}</span>
            <span className="tabular-nums">{w.percent_of_evaluated_value}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InputCaveats({ inputs }: { inputs: InsightsReport['inputs_available'] }) {
  const caveats: string[] = [];
  if (!inputs.nlv_available) {
    caveats.push('Net liquidating value (NLV) is not set, so position sizes are not available.');
  }
  if (!inputs.market_data_available) {
    caveats.push('Market data was unavailable when this report was generated.');
  }
  if (!inputs.knowledge_base_available) {
    caveats.push('The knowledge base was unavailable when this report was generated.');
  }
  if (caveats.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-amber-700/50 bg-amber-900/20 px-4 py-3"
      data-testid="input-caveats"
    >
      <p className="text-sm font-medium text-amber-300">Some inputs were unavailable</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-amber-400/90">
        {caveats.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
    </div>
  );
}

export function InsightsReportView({ report }: InsightsReportViewProps) {
  const { portfolio_summary, position_sizing, sector_regime, risk_concentration } = report;

  return (
    <div className="space-y-6" data-testid="insights-report">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full border border-primary-500/40 bg-primary-900/30 px-2.5 py-0.5 text-xs font-medium text-primary-300">
          Advisory
        </span>
        <span className="text-sm text-dark-400">
          as of <time dateTime={report.generated_at}>{formatDateTime(report.generated_at)}</time>
        </span>
      </div>

      <InputCaveats inputs={report.inputs_available} />

      <Section id="summary" title="Portfolio Summary">
        {portfolio_summary.length === 0 ? (
          <p className="text-sm text-dark-400">No open positions.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-dark-400">
                <th scope="col" className="pb-2 font-medium">Symbol</th>
                <th scope="col" className="pb-2 text-right font-medium">Net quantity</th>
                <th scope="col" className="pb-2 text-right font-medium">Cost basis</th>
              </tr>
            </thead>
            <tbody className="text-dark-200">
              {portfolio_summary.map((p) => (
                <tr key={p.symbol}>
                  <th scope="row" className="py-1 text-left font-medium">{p.symbol}</th>
                  <td className="py-1 text-right tabular-nums">{p.net_quantity}</td>
                  <td className="py-1 text-right tabular-nums">${p.cost_basis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section id="sizing" title="Position Sizing">
        {position_sizing.length === 0 ? (
          <p className="text-sm text-dark-400">No positions to size.</p>
        ) : (
          <>
            <ul className="space-y-1 text-sm text-dark-200">
              {position_sizing.map((p) => (
                <li key={p.symbol} className="flex justify-between gap-4">
                  <span>{p.symbol}</span>
                  <span className="tabular-nums">
                    {p.size_percent_of_nlv === null ? 'N/A' : `${p.size_percent_of_nlv}% of NLV`}
                  </span>
                </li>
              ))}
            </ul>
            {position_sizing.some((p) => p.size_percent_of_nlv === null) && (
              <p className="mt-2 text-xs text-dark-400">
                N/A means NLV is not set for this account, so a size percentage cannot be shown.
              </p>
            )}
          </>
        )}
      </Section>

      <Section id="regime" title="Sector & Regime">
        {sector_regime.available ? (
          <div className="space-y-1 text-sm text-dark-200">
            {sector_regime.quad && (
              <p>
                <span className="text-dark-400">Regime: </span>
                {sector_regime.quad}
              </p>
            )}
            {sector_regime.gip_summary && <p>{sector_regime.gip_summary}</p>}
          </div>
        ) : (
          <p className="text-sm text-dark-400">Regime research unavailable.</p>
        )}
        <div className="mt-4">
          <WeightList label="Sector weights" weights={report.sector_weights} />
        </div>
      </Section>

      <Section id="risk" title="Risk Concentration">
        <dl className="mb-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-dark-400">Largest position</dt>
            <dd className="text-dark-100">{risk_concentration.largest_position ?? 'N/A'}</dd>
          </div>
          <div>
            <dt className="text-dark-400">Largest sector</dt>
            <dd className="text-dark-100">{risk_concentration.largest_sector ?? 'N/A'}</dd>
          </div>
        </dl>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <WeightList label="Position shares" weights={risk_concentration.position_shares} />
          <WeightList label="Sector shares" weights={risk_concentration.sector_shares} />
        </div>
      </Section>

      <Section id="recommendations" title="Recommendations">
        <p className="mb-3 text-xs text-dark-400">
          Recommendations are advisory. Acting on a recommendation is a separate manual step
          outside this page.
        </p>
        {report.recommendations.length === 0 ? (
          <p className="text-sm text-dark-400">No recommendations are available.</p>
        ) : (
          <ul className="space-y-3">
            {report.recommendations.map((r, i) => (
              <li
                key={`${r.symbol}-${i}`}
                className="rounded-lg border border-dark-700/50 bg-dark-900/40 p-3"
                data-testid="recommendation"
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="font-semibold text-dark-100">{r.symbol}</span>
                  <span className="text-dark-300">
                    <span className="text-dark-400">Action: </span>
                    {r.suggested_action}
                  </span>
                  <span className="text-dark-300">
                    <span className="text-dark-400">Conviction: </span>
                    {r.conviction_level}
                  </span>
                </div>
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-dark-300">
                  {r.evidence.map((e, j) => (
                    <li key={j}>{e}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section id="provenance" title="Provenance">
        <ProvenancePanel provenance={report.provenance} />
      </Section>
    </div>
  );
}
